import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from '@mui/material'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { useDispatch } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { handleApiError } from 'src/utils/errorHandlers'
import { Contractor } from 'src/interfaces/contractor'
import { debounce } from '@mui/material/utils'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  AssignMCLiteToSubcontractor,
  FetchAvailableSubcontractorsForMCLite,
} from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { SearchMinor } from '@shopify/polaris-icons'
import { Pagination } from 'src/interfaces/table'
import { BasicAutocomplete } from 'src/components/shared/form/BasicAutocomplete'

interface Props {
  contractorId: string
  isOpen?: boolean
  onClose: () => void
  onSubmit: () => void
}

const MCLiteAssignedSubcontractorsModalComponent = ({
  contractorId,
  isOpen,
  onClose,
  onSubmit,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null)
  const [contractors, setContractors] = useState<Array<Contractor>>([])

  const container = useMemo(() => document.getElementById('root'), [])

  const optionsList = useMemo(() => {
    const res: Contractor[] = []

    if (selectedContractor && !contractors.some((item) => item.id === selectedContractor.id)) {
      res.push(selectedContractor)
    }

    res.push(...contractors)

    return res.map((item) => ({
      label: item.name,
      subLabel: item.externalId,
      value: item.id,
      contractor: item,
    }))
  }, [selectedContractor, contractors])

  const fetchContractors = useMemo(
    () =>
      debounce(async (searchQuery?: string) => {
        const payload: { data: Array<Contractor>; pagination: Pagination } =
          await createAsyncAction(
            dispatch,
            FetchAvailableSubcontractorsForMCLite.request({
              search: searchQuery,
              mcLiteId: contractorId,
            }),
          )

        setContractors(payload.data)
      }, 300),
    [contractorId],
  )

  useEffect(() => {
    if (!inputValue) {
      setContractors([])

      return
    }

    fetchContractors(inputValue)
  }, [inputValue])

  const handleAssign = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        AssignMCLiteToSubcontractor.request({
          mcLiteId: contractorId,
          subcontractorId: selectedContractor?.id,
        }),
      )

      onSubmit()

      onClose()

      showToast('Contractor assigned.', { variant: 'success' })
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [contractorId, selectedContractor?.id, onSubmit])

  const handleInputChange = useCallback((event: any, newInputValue: string) => {
    setInputValue(newInputValue)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedContractor(null)
    setInputValue('')
    setContractors([])
    onClose()
  }, [onClose])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur`}
    >
      <div
        data-testid="contractor-details-mc-lite-add-subcontractor-modal"
        css={tw`bg-[#F6F6F6] rounded-lg w-[360px] overflow-hidden`}
      >
        <ModalHeader
          className={tw`bg-white`}
          title="Add subcontractor"
          onClose={handleClose}
          closeButtonDataTestId="contractor-details-mc-lite-add-subcontractor-modal-close-btn"
        />
        <div css={tw`w-full bg-white`}>
          <div css={tw`p-4`}>
            <BasicAutocomplete
              name=""
              label="Select contractor"
              placeholder="Start typing name"
              noOptionsText="No contractors"
              options={optionsList}
              filterOptions={(x) => x}
              onInputChange={handleInputChange}
              onChange={(value) => {
                setSelectedContractor(
                  optionsList.find((item) => item.contractor.id === value)?.contractor || null,
                )
              }}
              startIcon={<SearchMinor width={20} />}
              inputDataTestId="contractor-details-mc-lite-add-subcontractor-autocomplete-input"
            />
          </div>
          <div
            css={tw`flex justify-between px-4 py-3 border-0 border-t border-solid border-[#EDEDED]`}
          >
            <Button
              variant="contained"
              color="grey"
              size="small"
              onClick={handleClose}
              loading={isLoading}
              className={tw`w-[100px]`}
              data-testid="contractor-details-mc-lite-add-subcontractor-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className={tw`w-[146px]`}
              disabled={!selectedContractor}
              variant="contained"
              size="small"
              loading={isLoading}
              onClick={handleAssign}
              data-testid="contractor-details-mc-lite-add-subcontractor-submit-btn"
            >
              Assign
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export const MCLiteAssignedSubcontractorsModal = memo(MCLiteAssignedSubcontractorsModalComponent)
